import {SelectedBunkHero} from './PurchasePathChoice';
import BunkOptions from './BunkOptions';
import PaymentChoices, {type PaymentChoice, type PlanMonths} from './PaymentChoices';
import {buildPaymentDueCents} from '../../../shared/buildPayment';
import {basicPromotion, BASIC_PROMO_END} from '../../../shared/basicPromotion';
import {metaBasicEntry} from '../../../shared/metaBasicEntry';
import {BASIC_BED_PRICE} from '../../../shared/basicBedPricing';
import SelectedBedSummary from './SelectedBedSummary';
import { measurePreviewEvent } from '../../services/metaConversionService';
import BunkReferenceImages, {useBunkReferencePhotos} from "./photo/BunkReferenceImages";
import BasicUpgradePicker from "./photo/BasicUpgradePicker";
import {basicUpgradeTotal, normalizeBasicUpgrades, type BasicUpgrades} from "../../../shared/basicUpgrades";
import DayDreamerProductSummary from "./DayDreamerProductSummary";
import {photoCombinationSnapshot} from './photo/ernst-fit';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, ChevronRight } from "lucide-react";
import {
  AGE_GROUPS,
  EMPTY_CONSULTATION,
  JOURNEY_STEPS,
  MAX_CLASSIC_GUESTS,
  consultationBunks,
  consultationRoomPlan,
  consultationRoomSnapshots,
  consultationQuickAnswers,
  consultationRecommendation,
  consultationSnapshot,
  infantsOnly,
  needsLayoutReview,
  normalizeConsultation,
  suggestedPlanMonths,
  repeatBack,
  repeatBackSummary,
  stageReady,
  type ConsultativeAnswers,
  type JourneyIntent,
} from "./consultative-flow";
import { planGuestRooms, describeBunkMix } from "./guest-room-plan";
import { blissZipServiceStatus } from "../../utils/blissServiceArea";
import type { QuickAnswers } from "./BlissQuickStart";
import type { BlissRoomPlanContact } from "../../config/blissRoomPlan";
import type { PhotoPreviewDraft } from "./photo/RoomPhotoPreview";
import type { BunkSelection } from "./BunkOptions";
import type { ConfiguratorSnapshot } from "./design/configurator/integration";
import { estimatePlanRange } from "./design/configurator/room/quote-range";
import { getPricing } from "../../data/pricing";
import {
  CUSTOM_WORLDS,
  CUSTOM_INVESTMENT_NOTICE,
} from "../../config/blissCustomDiscovery";
import { BLISS_PORTFOLIO_PROJECTS } from "../../config/blissPortfolio";
import { BED_OPTIONS } from "./design/configurator/room/beds";
import PackageScopeReview from "./PackageScopeReview";
import PurchaseAgreement from "./PurchaseAgreement";
import {
  PURCHASE_AGREEMENT_VERSION,
  PURCHASE_HIGHLIGHTS,
} from "../../../shared/purchaseAgreement";
import type { OrderConsent } from "../../../shared/orderConsent";
import { DESIGN_ONLY_DESCRIPTION } from "../../../shared/designOnlyService";
import "./BlissConsultativeJourney.css";
import RoomInspirationCollage from "./RoomInspirationCollage";
import BlissPriceIntroduction from "./BlissPriceIntroduction";
import ErnstLandingFeature from "./ErnstLandingFeature";
import {ERNST_LANDING_MODEL,LANDING_MODELS,CUSTOM_STARTING_PRICE,CASTLE_STARTING_PRICE,type LandingModel} from "./landing-models";
import { designOnlyPrice } from "../../../shared/designOnlyPricing";

import { installmentCount } from "../../../shared/monthlyPrepayment";
const Photo = lazy(() => import("./photo/RoomPhotoPreview"));
const MonthlyPayment = lazy(() => import("./ConsultativeMonthlyPayment"));
const DesignOnlyPayment = lazy(() => import("./ConsultativeDesignOnlyPayment"));
const Proposal = lazy(() => import("./PlanProposalEntry"));
const RoomReview = lazy(() => import("./RoomReviewCheckout"));
const SINGLE_CLASSIC_IMAGE =
  "/bliss-basic/gallery/16-sage-room-white-bunk-styled-render.jpg";
const PAIRED_CLASSIC_IMAGE =
  "/bliss-basic/gallery/10-coastal-double-bunks-waterfront.jpg";
// Deliberate curation: these built-in rooms have distinct architecture, not Classic units.
const CUSTOM_PROJECTS = BLISS_PORTFOLIO_PROJECTS.filter((p) =>
  [82662, 82316].includes(p.id),
);
const money = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
type Props = {
  requireVerificationCall?: boolean;
  contact?: BlissRoomPlanContact;
  initialAnswers?: QuickAnswers;
  initialPhoto?: PhotoPreviewDraft;
  onSubmit: (
    contact: BlissRoomPlanContact,
    answers: QuickAnswers,
    snapshot: ConfiguratorSnapshot,
    photo?: PhotoPreviewDraft,
    completedStep?: 6 | 7,
  ) => Promise<string>;
};
function readDraft(initial?: QuickAnswers, metaBasic = false) {
  // A plain visit is a new room. Only an explicitly opened saved project may
  // supply initial answers; legacy browser drafts must not resurrect orders.
  return {
    answers: normalizeConsultation({
      ...EMPTY_CONSULTATION,
      direction: metaBasic ? "classic" : "custom",
      inspirationId: metaBasic ? undefined : "82662",
      ...initial?.consultation,
      entryMode: "transformation",
    }),
    step: 5,
    transformationFirst: true,
  };
}
function Choice({
  selected,
  onClick,
  children,
  detail,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  detail?: string;
}) {
  return (
    <button
      type="button"
      className={`bcj-choice${selected ? " is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span>
        <strong>{children}</strong>
        {detail && <small>{detail}</small>}
      </span>
      <span className="bcj-choice-mark" aria-hidden="true">
        {selected ? <Check size={16} /> : <ChevronRight size={17} />}
      </span>
    </button>
  );
}
export default function BlissConsultativeJourney({
  contact,
  initialAnswers,
  initialPhoto,
  requireVerificationCall = false,
  onSubmit,
}: Props) {
  const [metaEntry] = useState(() => !initialAnswers && !initialPhoto && typeof window !== 'undefined'
    ? metaBasicEntry(window.location.search) : null);
  const [customCheckout,setCustomCheckout]=useState(false);
  const [changingPaymentMethod,setChangingPaymentMethod]=useState(false);
  const [promoInput,setPromoInput] = useState(metaEntry?.promoCode || '');
  const [promoCode,setPromoCode] = useState(metaEntry?.promoCode || '');
  const [promoError,setPromoError] = useState('');
  const [initial] = useState(() => readDraft(initialAnswers, !!metaEntry));
  const [answers, setAnswers] = useState(initial.answers),
    [step, setStep] = useState(initial.step);
  const [largeGroup, setLargeGroup] = useState(
    (initial.answers.sleepers ?? 0) > 20,
  );
  const [largeGuestInput, setLargeGuestInput] = useState(
    String(initial.answers.sleepers ?? 21),
  );
  const [portfolio, setPortfolio] = useState<"custom" | "one_of_a_kind" | null>(
      null,
    ),
    [galleryIndex, setGalleryIndex] = useState(0);
  const [exploring, setExploring] = useState(true),
    [showGallery, setShowGallery] = useState(false);
  const [details, setDetails] = useState<BlissRoomPlanContact>(contact ?? {});
  const phoneRequested = requireVerificationCall || answers.contactPreference === "phone" || answers.contactPreference === "both";
  const contactDestination = answers.contactPreference === "both"
    ? `${details.email} and ${details.phone}`
    : answers.contactPreference === "phone" ? details.phone : details.email;
  const [contactCaptured, setContactCaptured] = useState(false);
  const [showSalesLetter, setShowSalesLetter] = useState(false);
  const [priceRevealed, setPriceRevealed] = useState(false);
  useEffect(() => { if (step === 6) measurePreviewEvent('contact_view'); }, [step]);
  useEffect(() => { if (priceRevealed && !requireVerificationCall) measurePreviewEvent('price_view'); }, [priceRevealed, requireVerificationCall]);
  const transformationFirst = initial.transformationFirst;
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false),
    [savedPlanId, setSavedPlanId] = useState<string>();
  const [accepted, setAccepted] = useState(false),
    [consent, setConsent] = useState<OrderConsent>();
  const [canContact, setCanContact] = useState(false),
    [signer, setSigner] = useState("");
  const [restoredPhoto] = useState<PhotoPreviewDraft | undefined>(() => initialPhoto);
  const [fastTrack,setFastTrack]=useState(false);
  const [openPreviewEntry,setOpenPreviewEntry]=useState(false);
  const [landingPhotoFile,setLandingPhotoFile]=useState<File>();
  const [landingStarted,setLandingStarted]=useState(!!restoredPhoto?.photo);
  useEffect(() => { if (landingStarted) measurePreviewEvent('preview_start'); }, [landingStarted]);
  const [landingModel,setLandingModel]=useState<LandingModel>(()=>initial.answers.direction==='classic'?LANDING_MODELS.Basic.find(m=>m.choice===(initialAnswers?.choice ?? metaEntry?.choice))??LANDING_MODELS.Basic[0]:initial.answers.inspirationId==='storybook'?LANDING_MODELS.Themes[0]:ERNST_LANDING_MODEL);
  function photoBunks(d?: PhotoPreviewDraft): BunkSelection[] | null {
    if (!d?.beds.length || d.beds.some(b => (b.layout === "ernst" || b.layout === "castle")) || (!transformationFirst && (d.beds.length !== 1 || consultationBunks(initial.answers).length !== 1))) return null;
    return d.beds.map(b => ({choice:b.choice,finish:b.finish??"gloss_white",customColorCode:b.customColorCode,upgrades:normalizeBasicUpgrades(b.upgrades??{})}));
  }
  const [adjustedBunks, setAdjustedBunks] = useState<BunkSelection[] | null>(
    () => photoBunks(restoredPhoto),
  );
  const photo = useRef<PhotoPreviewDraft | undefined>(restoredPhoto);
  function rememberPhoto(d: PhotoPreviewDraft) {
    d = {...d,beds:d.beds.map(b=>({...b,upgrades:normalizeBasicUpgrades(b.upgrades??{})}))};
    photo.current = d;
    if (d.beds.some(b => (b.layout === "ernst" || b.layout === "castle"))) setAdjustedBunks(null);
    if (transformationFirst && d.beds[0]?.layout) {
      const direction = d.beds[0].layout === "castle" ? "one_of_a_kind" : d.beds[0].layout === "ernst" ? "custom" : "classic";
      setAnswers(prior => prior.direction === direction && (direction !== "custom" || prior.inspirationId === "82662") ? prior : normalizeConsultation({...prior, direction, inspirationId: direction === "one_of_a_kind" ? "storybook" : direction === "custom" ? "82662" : undefined}));
    }
    if (!d.beds.some(b => (b.layout === "ernst" || b.layout === "castle")) && d.beds.length > 0 && (transformationFirst || (d.beds.length === 1 && consultationBunks(answers).length === 1))) {
      const next = d.beds.map(b => ({choice:b.choice,finish:b.finish??"gloss_white",customColorCode:b.customColorCode,upgrades:normalizeBasicUpgrades(b.upgrades??{})})) as BunkSelection[];
      setAdjustedBunks((prior) =>
        JSON.stringify(prior) === JSON.stringify(next) ? prior : next,
      );
    }
  }
  const heading = useRef<HTMLHeadingElement>(null);
  const portfolioHost = useRef<HTMLElement>(null);
  const browseHeading = useRef<HTMLHeadingElement>(null);
  const toolHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (transformationFirst && exploring && !showGallery) return;
    const target = showGallery
      ? browseHeading.current
      : exploring
        ? toolHeading.current
        : null;
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [showGallery, exploring]);
  useEffect(() => {
    if (portfolio)
      portfolioHost.current?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
  }, [portfolio]);
  const recommendation = useMemo(
    () => consultationRecommendation(answers),
    [answers],
  );
  const roomPlan = consultationRoomPlan(answers);
  // The photo-first journey sells the user's selected beds, not a capacity
  // recommendation recovered from an older questionnaire. A missing photo
  // draft must never silently multiply the order or add suggested upgrades.
  const bunks: BunkSelection[] = adjustedBunks ?? (transformationFirst
    ? [{ choice: landingModel.choice, finish: "gloss_white", upgrades: {} }]
    : consultationBunks(answers));
  const selectionPhotos = useBunkReferencePhotos();
  const [editingExtras, setEditingExtras] = useState(false);
  const [exploreTier, setExploreTier] = useState<'classic'|'custom'|'one_of_a_kind'|null>(null);
  function selectTierDesign(direction:'classic'|'custom'|'one_of_a_kind', choice=0, inspirationId?:string) {
    setSubmitted(false);
    setExploreTier(null);
    if((direction !== 'one_of_a_kind' || inspirationId==='storybook') && photo.current) {
      const previous = photo.current.beds[0];
      rememberPhoto({...photo.current,beds:[{...previous,choice,layout:inspirationId==='storybook'?'castle':direction==='custom'?'ernst':'basic',upgrades:{}}]});
    }
    update({direction,inspirationId,intent:undefined});
    if(direction==='classic'||inspirationId==='storybook') {setExploring(true);setShowGallery(false);go(5);}
  }
  function editBunkExtras(index:number, upgrades:BasicUpgrades) {
    if (consent || busy) return;
    upgrades = normalizeBasicUpgrades(upgrades);
    const next = bunks.map((b,i)=>i===index?{...b,upgrades}:b);
    setAdjustedBunks(next);
    if(photo.current) rememberPhoto({...photo.current,beds:photo.current.beds.map((b,i)=>i===index?{...b,upgrades}:b)});
    setSigner('');
    update({});
  }
  function removeCustomColor(index:number) {
    if (consent || busy) return;
    setAdjustedBunks(bunks.map((b,i)=>i===index?{...b,finish:"gloss_white",customColorCode:undefined}:b));
    if(photo.current) rememberPhoto({...photo.current,beds:photo.current.beds.map((b,i)=>i===index?{...b,finish:"gloss_white",customColorCode:undefined}:b)});
    setSigner('');
    update({});
  }
  function removeBunk(index: number) {
    if (!transformationFirst || consent || busy || bunks.length <= 1 || index < 0 || index >= bunks.length) return;
    setAdjustedBunks(bunks.filter((_, i) => i !== index));
    if (photo.current) {
      const beds = photo.current.beds.filter((_, i) => i !== index);
      rememberPhoto({
        ...photo.current,
        beds,
        basicBackup: beds,
        basicCombination: false,
        active: beds.some(b => b.id === photo.current!.active) ? photo.current.active : beds[0].id,
      });
    }
    update({ intent: undefined });
  }
  function backToRoom() { setCustomCheckout(false); update({intent:undefined}); setExploring(true); setShowGallery(false); go(5); }
  const classicImage =
    bunks.length > 1 ? PAIRED_CLASSIC_IMAGE : SINGLE_CLASSIC_IMAGE;
  const galleryRooms =
    answers.direction === "classic"
      ? [
          {
            image: SINGLE_CLASSIC_IMAGE,
            name: "A calm, layered Classic",
            description:
              "White joinery, softly layered neutral linens and stair access at the foot of the bed. Styled inspiration; your layout is confirmed individually.",
          },
          {
            image: PAIRED_CLASSIC_IMAGE,
            name: "Room for everyone",
            description:
              "Two parallel Classic units frame a central staircase, with blue-grey linens bringing a quiet coastal note. Illustrative reference; your room determines the final arrangement.",
          },
        ]
      : answers.direction === "one_of_a_kind"
        ? CUSTOM_WORLDS.map((room) => ({
            image: room.image,
            name: "A world of their own",
            description:
              "Concept inspiration: distinctive architecture, with a final design tailored to your room.",
          }))
        : CUSTOM_PROJECTS.map((room) => ({
            image: room.image,
            name: room.name,
            description:
              "From the Bliss portfolio: individual spaces with their own architectural character.",
          }));
  const galleryRoom = galleryRooms[galleryIndex % galleryRooms.length];
  const snapshot = {
    ...(transformationFirst ? photoCombinationSnapshot(bunks) : consultationSnapshot(answers, bunks)),
    quoteSettings: {
      zip: /^\d{5}$/.test(details.zip ?? "") ? details.zip! : "",
      timeline: "standard" as const,
    },
  };
  const roomSnapshots = (transformationFirst ? [{
    ...consultationRoomPlan(answers).rooms[0],
    name: 'Your room',
    choices: bunks.map(b=>b.choice),
    configuratorSnapshot: photoCombinationSnapshot(bunks),
  }] : consultationRoomSnapshots(answers, bunks)).map(
    (room) => ({
      ...room,
      configuratorSnapshot: {
        ...room.configuratorSnapshot,
        quoteSettings: snapshot.quoteSettings,
      },
    }),
  );
  const hasCustomPreview = !!photo.current?.beds.some(b => (b.layout === "ernst" || b.layout === "castle"));
  const pricing = useMemo(() => {
    try {
      if (answers.direction !== "classic" || hasCustomPreview || (!transformationFirst && !answers.sleepers) || infantsOnly(answers))
        return null;
      return estimatePlanRange(
        roomSnapshots.map((r) => r.configuratorSnapshot),
        "basic",
        getPricing(),
      );
    } catch {
      return null;
    }
  }, [
    answers.direction,
    hasCustomPreview,
    JSON.stringify(roomSnapshots),
    answers.sleepers,
    answers.groups.join("|"),
  ]);
  const packageId = bunks.some((b) => basicUpgradeTotal(b.upgrades) > 0)
    ? "premier"
    : "standard";
  useEffect(() => {
    if (!promoCode) return;
    const check = () => { try { basicPromotion(promoCode,'basic',1); } catch { setPromoCode(''); setPromoError('BEDOCTOBER has expired. Your order has returned to regular pricing.'); setAccepted(false); setSigner(''); } };
    const timer = window.setInterval(check,1000);
    window.addEventListener('focus',check);
    return () => { window.clearInterval(timer); window.removeEventListener('focus',check); };
  }, [promoCode]);
  const regularPkg = pricing?.packages.find((p) => p.id === packageId);
  let promotion: ReturnType<typeof basicPromotion>;
  try { if (answers.direction === 'classic') promotion = basicPromotion(promoCode, 'basic', bunks.length); } catch { /* Expired codes are excluded and rechecked on the server. */ }
  const pkg = regularPkg && promotion ? {...regularPkg,total:regularPkg.total-promotion.savings} : regularPkg;
  const requiresReview = needsLayoutReview(answers) || !!pricing?.pending;
  // Room-fit review is part of the paid design process, not a sales eligibility gate.
  // Keep the payment invitation visible; validate price, ZIP and consent at checkout.
  const hasPricedClassic =
    answers.direction === "classic" &&
    !!pkg &&
    (transformationFirst ||
      answers.sharing !== "undecided" ||
      answers.sleepers === 1);
  const depositAvailable =
    hasPricedClassic &&
    !pricing?.pending &&
    blissZipServiceStatus(details.zip ?? "") === "full_service";
  const designServicePrice = hasPricedClassic
    ? designOnlyPrice(pricing!.packages.map((p) => p.total))
    : 0;
  const earliestInstallMonth = useMemo(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + answers.planMonths);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  }, [answers.planMonths]);
  const rushOrder = answers.timing === "Rush Order — Full Payment";
  const paymentMode = rushOrder ? "full" : "deposit";
  const paymentDue = buildPaymentDueCents(pkg?.total ?? 0, false, paymentMode) / 100;
  const monthlyEstimate = hasPricedClassic
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(
        Math.max(0, pkg!.total - answers.upfrontAmount) /
          installmentCount(answers.paymentFrequency, answers.planMonths),
      )
    : null;
  const selectedTheme = CUSTOM_WORLDS.find(w => w.id === answers.inspirationId) ?? CUSTOM_WORLDS[0];
  const selectedCustom = CUSTOM_PROJECTS.find(p => String(p.id) === answers.inspirationId) ?? CUSTOM_PROJECTS[0];
  const customName = answers.direction === "one_of_a_kind" ? (selectedTheme.id === "voyage" ? "Ship Bunks" : selectedTheme.id === "storybook" ? "Castle Bunks" : selectedTheme.title) : "Ernst";
  const catalogPurchasable=answers.direction==='custom'||(answers.direction==='one_of_a_kind'&&selectedTheme.id==='storybook');
  const customImage = answers.direction === "one_of_a_kind" ? selectedTheme.image : selectedCustom.image;
  const customDescription = answers.direction === "one_of_a_kind" ? selectedTheme.detail : "Double perpendicular floating bunks with a central staircase.";
  const savedDesign = step === 7 && priceRevealed && (!answers.intent || (answers.intent === 'designer' && submitted)) && answers.direction !== "classic";
  const compactCatalog = savedDesign && catalogPurchasable && customCheckout && contactCaptured && !!savedPlanId;
  const progressIndex = transformationFirst ? (step === 7 ? (answers.intent ? 3 : 2) : showSalesLetter ? 2 : step === 6 ? 1 : 0) : step - 1;
  const title = [
    "Who will be visiting?",
    "Let’s make it comfortable.",
    "Here’s what we’re hearing.",
    "A thoughtful place to begin.",
    "Transform your room.",
    "Let’s save your project.",
    answers.intent === "room_review" ? "Your Room Review." : answers.intent === "designer" ? (submitted ? "Request sent." : "Ask us for a price.") : hasPricedClassic || compactCatalog ? "Your room. Your price." : "Your room is saved.",
  ][step - 1];
  const intro = [
    "Start with the people. We’ll help with the room.",
    "Bunk beds tailored to the people who sleep in them.",
    "Before we suggest a direction, does this sound like your room?",
    "Based on what you’ve shared, we recommend starting with Classic.",
    "See what’s possible. Then try it in a photo of your own room. Free. No signup. No need to tidy up.",
    "Please provide your contact info so that we can save your preferences and project details.",
    answers.intent === "room_review" ? "30 minutes with a Bliss designer." : answers.intent === "designer" ? "We’ll review your room and contact you with the next steps." : hasPricedClassic ? "Check your price. Then choose how to pay." : "Let’s price your custom room.",
  ][step - 1];
  const query = new URLSearchParams(window.location.search);
  // Only known, reviewed project IDs can select an ad image; never accept arbitrary URLs.
  const adProject = CUSTOM_PROJECTS.find(
    (p) => String(p.id) === query.get("ad_model"),
  );
  const featured = adProject ?? CUSTOM_PROJECTS[0];
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    setError("");
  }, [step, showSalesLetter]);
  useEffect(() => {
    setAccepted(false);
    setConsent(undefined);
  }, [JSON.stringify(bunks), pkg?.total]);
  function update(patch: Partial<ConsultativeAnswers>) {
    setAnswers((a) =>
      normalizeConsultation({
        ...a,
        ...patch,
        ...("groups" in patch ||
        "sleepers" in patch ||
        "sharing" in patch ||
        "guestBuild" in patch ||
        "soloGuests" in patch ||
        "dedicatedRooms" in patch ||
        "mixedRooms" in patch ||
        "access" in patch ||
        "legroom" in patch ||
        "purpose" in patch
          ? { confirmed: false, intent: undefined }
          : {}),
      }),
    );
    if (
      "groups" in patch ||
      "sleepers" in patch ||
      "sharing" in patch ||
      "guestBuild" in patch ||
      "soloGuests" in patch ||
      "dedicatedRooms" in patch ||
      "mixedRooms" in patch ||
      "access" in patch ||
      "legroom" in patch
    ) {
      setAdjustedBunks(null);
      photo.current = undefined;
      try {
        sessionStorage.removeItem("bliss_consultative_photo_v1");
      } catch {}
    }
    setSubmitted(false);
    setError("");
  }
  function go(next: number) {
    setShowSalesLetter(false);
    setStep(
      Math.min(contactCaptured && priceRevealed ? 7 : 6, Math.max(1, next)),
    );
    if (next === 6) update({ intent: undefined });
    setPortfolio(null);
    setGalleryIndex(0);
  }
  function chooseIntent(intent: JourneyIntent) {
    setCustomCheckout(false);
    update({ intent });
    go(7);
  }
  function changePaymentMethod() {
    setChangingPaymentMethod(true);
    setAccepted(false); setSigner(""); setConsent(undefined); setSubmitted(false);
    update({intent: undefined});
    go(7);
    window.scrollTo({top:0, behavior:"instant" as ScrollBehavior});
  }
  function selectPayment(choice: PaymentChoice) {
    setChangingPaymentMethod(false);
    setAccepted(false);
    setSigner("");
    setConsent(undefined);
    setSubmitted(false);
    update({
      intent: choice === "monthly" ? "monthly_plan" : "deposit",
      timing: choice === "full" ? "Rush Order — Full Payment" : choice === "monthly" ? `Pay over time — ${answers.planMonths} months` : "50/50 — standard scheduling",
      ...(choice === "monthly" ? {paymentFrequency: "monthly" as const, upfrontAmount: 0, installationMonth: ""} : {}),
    });
    go(7);
    window.scrollTo({top:0, behavior:"instant" as ScrollBehavior});
  }
  function selectPlanMonths(months: PlanMonths) {
    setAccepted(false); setSigner(""); setConsent(undefined);
    update({planMonths: months, paymentFrequency: "monthly", upfrontAmount: 0, installationMonth: ""});
  }
  function chooseDirection(
    direction: ConsultativeAnswers["direction"],
    inspirationId?: string,
  ) {
    update({ direction, inspirationId, intent: undefined });
    setPortfolio(null);
    go(5);
  }
  async function submit(event: React.FormEvent, completedStep: 6 | 7 = 7, intent?: JourneyIntent) {
    event.preventDefault();
    if (busy || !canContact) return;
    if (
      !details.name?.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email?.trim() ?? "") ||
      !/^\d{5}$/.test(details.zip ?? "")
    ) {
      setError(
        "Please enter your name, a valid email and your five-digit project ZIP.",
      );
      return;
    }
    if (completedStep === 7 && !contactCaptured) {
      go(6);
      return;
    }
    if (phoneRequested && (details.phone?.replace(/\D/g, "").length ?? 0) < 10) {
      setError("Please enter a phone number with area code so we can call you.");
      return;
    }
    if (
      !requireVerificationCall && completedStep === 7 &&
      answers.intent === "deposit" &&
      (!depositAvailable || !accepted || !signer.trim())
    )
      return;
    if (
      !requireVerificationCall && completedStep === 7 &&
      answers.intent === "monthly_plan" &&
      (!/^20\d{2}-(0[1-9]|1[0-2])$/.test(answers.installationMonth) ||
        answers.installationMonth < earliestInstallMonth)
    ) {
      setError(
        "Choose an installation month after your selected payment period so the balance can be paid beforehand.",
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const quick = consultationQuickAnswers(
        requireVerificationCall ? { ...answers, intent: "designer", contactPreference: "phone" } : completedStep === 6 ? { ...answers, intent: undefined } : intent ? {...answers, intent} : answers,
        bunks,
      );
      quick.selectedDesignImages = [
        {
          url:
            answers.direction === "classic"
              ? classicImage
              : answers.direction === "one_of_a_kind"
                ? (
                    CUSTOM_WORLDS.find((w) => w.id === answers.inspirationId) ??
                    CUSTOM_WORLDS[0]
                  ).image
                : (
                    CUSTOM_PROJECTS.find(
                      (p) => String(p.id) === answers.inspirationId,
                    ) ?? featured
                  ).image,
          label:
            answers.direction === "classic"
              ? "Classic style reference"
              : answers.direction === "one_of_a_kind"
                ? "Custom concept inspiration"
                : "Bliss custom project inspiration",
        },
      ];
      const id = await onSubmit(
        details,
        quick,
        snapshot,
        photo.current,
        requireVerificationCall ? 7 : completedStep,
      );
      if (!id)
        throw new Error("Your project could not be saved. Please try again.");
      setSavedPlanId(id);
      if (requireVerificationCall) {
        setContactCaptured(true);
        setSubmitted(true);
        setAnswers(prior => ({ ...prior, intent: "designer", contactPreference: "phone" }));
        setShowSalesLetter(false);
        setPriceRevealed(true);
        setStep(7);
        return;
      }
      if (completedStep === 6) {
        setContactCaptured(true);
        if (catalogPurchasable && !requireVerificationCall) setCustomCheckout(true);
        setSubmitted(false);
        setChangingPaymentMethod(false);
        setAnswers((prior) => ({ ...prior, intent: hasPricedClassic && prior.timing === "Rush Order — Full Payment" ? "deposit" : undefined }));
        setShowSalesLetter(false);
        setPriceRevealed(true);
        setStep(7);
        return;
      }
      if (answers.intent === "deposit" && pkg)
        setConsent({
          paymentMode,
          signerName: signer.trim(),
          total: pkg.total,
          packageId,
          agreementVersion: PURCHASE_AGREEMENT_VERSION,
        });
      if (intent) setAnswers(prior => ({...prior, intent}));
      setSubmitted(true);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not save your project. Your answers are still here. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  const checkoutCode = (
    <div className="bcj-promo">
                        <label htmlFor="bcj-promo-code">Checkout code</label>
                        <div className="bcj-promo-controls"><input id="bcj-promo-code" value={promoInput} disabled={busy || !!consent} autoCapitalize="characters" onChange={e=>setPromoInput(e.target.value)} /><button type="button" className="bcj-text-button" disabled={busy || !!consent} onClick={()=>{try{const offer=basicPromotion(promoInput,'basic',bunks.length);setPromoCode(offer?.code??'');setPromoError('');setAccepted(false);setSigner('');}catch(e){setPromoError((e as Error).message);}}}>Apply</button></div>
                        {promoError&&<p role="alert">{promoError}</p>}
                        {promotion&&<p role="status">BEDOCTOBER applied · You save {money(promotion.savings)}. <button type="button" className="bcj-text-button" disabled={busy || !!consent} onClick={()=>{setPromoCode('');setPromoInput('');setAccepted(false);setSigner('');}}>Remove code</button></p>}
                        {Date.now()<BASIC_PROMO_END&&<p className="bcj-note">25% off Bliss Basic base bunk sets with BEDOCTOBER. Ends October 31, 2026 at 11:59 p.m. Central. Extras and delivery excluded.</p>}
                      </div>
  );
  const contactFields = (
    <fieldset className="bcj-contact" disabled={busy}>
      <legend>Please Provide your Information.</legend>
      {!requireVerificationCall && <label>
        How would you like us to contact you?
        <select value={answers.contactPreference ?? "email"} onChange={e => update({contactPreference: e.target.value as "email" | "phone" | "both"})}>
          <option value="email">Email</option>
          <option value="phone">Phone</option>
          <option value="both">Email and phone</option>
        </select>
      </label>}
      {(["name", "email", "phone", "zip"] as const).map((key) => (
        <label key={key}>
          {
            {
              name: "Your name",
              email: "Email",
              phone: phoneRequested ? "Phone" : "Phone (optional)",
              zip: "Project ZIP",
            }[key]
          }
          <input
            required={key !== "phone" || phoneRequested}
            value={details[key] ?? ""}
            type={key === "email" ? "email" : key === "phone" ? "tel" : "text"}
            autoComplete={
              {
                name: "name",
                email: "email",
                phone: "tel",
                zip: "postal-code",
              }[key]
            }
            pattern={key === "zip" ? "[0-9]{5}" : undefined}
            maxLength={key === "zip" ? 5 : 120}
            onChange={(e) => setDetails({ ...details, [key]: e.target.value })}
          />
        </label>
      ))}
      <label>
        When are you hoping to begin?
        <select
          value={answers.timing}
          onChange={(e) => update({ timing: e.target.value, planMonths: suggestedPlanMonths(e.target.value), paymentFrequency: "monthly", installationMonth: "" })}
        >
          <option value="">Still exploring</option>
          <option value="As soon as practical">ASAP — as soon as practical</option>
          <option>In 1–3 months</option>
          <option>In 3–6 months</option>
          <option>Later this year</option>
          <option>No rush — planning a year ahead</option>
          <option>Rush Order — Full Payment</option>
          <option>ASAP — up to 3 months</option>
          <option>Flexible — up to 6 months</option>
          <option>Plenty of time — up to 9 months</option>
        </select>
      </label>
    </fieldset>
  );
  const contactPermission = (
    <label className="bcj-check">
      <input
        required
        type="checkbox"
        checked={canContact}
        onChange={(e) => setCanContact(e.target.checked)}
      />
      Bliss may contact me about this project and use the details I’ve shared to
      prepare for our conversation.
    </label>
  );
  const summary = (
    <p className="bcj-repeat">
      {transformationFirst
        ? "Your room photo, selected bunk and options stay with your project. We’ll confirm measurements and sleeping needs before production."
        : repeatBack(answers)}
    </p>
  );
  const galleryItems =
    portfolio === "one_of_a_kind"
      ? CUSTOM_WORLDS.map((w) => ({
          id: w.id,
          image: w.image,
          name: w.title,
          detail: w.detail,
        }))
      : CUSTOM_PROJECTS.map((p) => ({
          ...p,
          id: String(p.id),
          detail:
            p.id === 82662
              ? "Warm timber cross rails, white built-in alcoves, and a central stair bring rhythm to this room."
              : p.id === 82316
                ? "Explore the room’s built-in architecture and the details that give each sleeping place its own character."
                : "A custom room shaped around the space and its sleeping places.",
        }));
  const item = galleryItems[galleryIndex % galleryItems.length];
  const monthlySession = new URLSearchParams(window.location.search).get(
    "monthly_session",
  );
    const selectedProducts = <section className="bcj-selected-product" aria-label="Your selected bed">
          <p className="bcj-extras-intro">Your selections are included below. Remove any extra to lower your total.</p><div className="bcj-selected-heading"><h2 className="bcj-selected-title">Your selected extras</h2><button type="button" className="bcj-text-button" disabled={!!consent || busy} onClick={backToRoom}>Back to my room</button></div>
          {answers.direction === 'classic' ? bunks.map((b,index)=><SelectedBedSummary key={index}
            image={<BunkReferenceImages choices={[b.choice]} photos={selectionPhotos}/>}
            title={`${bunks.length>1?`${index+1}. `:''}${BED_OPTIONS[b.choice]?.name??'Bunk bed'}`}
            finish={`Bliss Basic · ${b.finish==='custom_color'?'Custom Color':b.finish==='matte_white'?'Matte White':'Gloss White'}`}
            upgrades={b.upgrades} finishId={b.finish} number={index+1} disabled={!!consent || busy}
            onRemoveFinish={()=>removeCustomColor(index)} onRemove={key=>editBunkExtras(index,{...b.upgrades,[key]:false,...(key==='stairs'?{stairDrawers:false}:{})})}>
            {transformationFirst && bunks.length > 1 && <div className="bcj-selected-actions"><button type="button" className="bcj-text-button" disabled={!!consent || busy} onClick={() => removeBunk(index)} aria-label={`Remove bunk ${index + 1}`}>Remove bunk {index + 1}</button></div>}
            {editingExtras&&<BasicUpgradePicker title="Add or remove extras" value={b.upgrades} onChange={upgrades=>editBunkExtras(index,upgrades)} variant="rows"/>}
          </SelectedBedSummary>):<div className="bcj-selected-unit"><img src={answers.direction==='custom'?featured.image:(CUSTOM_WORLDS.find(w=>w.id===answers.inspirationId)??CUSTOM_WORLDS[0]).image} alt="Selected bed example"/><div><h2>{answers.direction==='custom'?'Ernst':answers.inspirationId==='voyage'?'Ship bed':answers.inspirationId==='storybook'?'Castle bed':'Theme bed'}</h2><p>{answers.direction==='custom'?'Bliss Custom':'Bliss Themes'} · Example design</p></div></div>}
          <div className="bcj-selected-actions"><button type="button" className="bcj-text-button" disabled={!!consent || busy} onClick={backToRoom}>Change bed</button>{answers.direction==='classic'&&<button type="button" className="bcj-text-button" disabled={!!consent || busy} aria-expanded={editingExtras} onClick={()=>setEditingExtras(!editingExtras)}>{editingExtras?'Done editing extras':'Edit extras'}</button>}</div>
        </section>;

  if (monthlySession?.startsWith("cs_"))
  return (
      <main className="bcj">
        <header className="bcj-header">
          <a href="https://blissbunkbeds.com">
            <img
              className="bcj-brand-logo"
              src="/bliss-bedrooms-logo-transparent.png"
              alt="Bliss Bedrooms"
              width="496"
              height="344"
            />
          </a>
        </header>
        <div className="bcj-shell">
          <Suspense
            fallback={<p role="status">Confirming your payment plan…</p>}
          >
            <MonthlyPayment sessionId={monthlySession} />
          </Suspense>
        </div>
      </main>
    );
  if(transformationFirst&&step===5&&!landingStarted)return <ErnstLandingFeature requireVerificationCall={requireVerificationCall} metaBasic={!!metaEntry} adHeadline={metaEntry?.headline} adCreative={metaEntry?.creative} promoCode={promoCode} initialPreview={openPreviewEntry} initialModel={answers.direction==='classic'?{...(LANDING_MODELS.Basic.find(m=>m.choice===bunks[0]?.choice)??landingModel),ladderFlipped:landingModel.ladderFlipped}:landingModel} onCustomize={model=>{
    setOpenPreviewEntry(false);setLandingModel(model);setLandingPhotoFile(undefined);setFastTrack(true);
    // A collection selection starts one explicit set; returning from preview preserves its draft.
    if(answers.direction!=='classic' || bunks[0]?.choice!==model.choice){setAdjustedBunks([{choice:model.choice,finish:'gloss_white',upgrades:{}}]);photo.current=undefined;}else setAdjustedBunks(bunks);
    update({direction:'classic',inspirationId:undefined,intent:undefined});
    setLandingStarted(true);setShowGallery(false);setExploring(true);window.scrollTo({top:0,behavior:'instant' as ScrollBehavior});
  }} onPreview={(model,file)=>{
    const keepBasicSelections=model.layout==='basic' && answers.direction==='classic' && bunks[0]?.choice===model.choice;
    setOpenPreviewEntry(false);setLandingPhotoFile(file);setFastTrack(false);
    setLandingModel(model);
    const direction=model.layout==='ernst'?'custom':model.layout==='castle'?'one_of_a_kind':'classic';
    setAdjustedBunks(model.layout==='basic'?(keepBasicSelections?bunks:[{choice:model.choice,finish:'gloss_white',upgrades:{}}]):null);
    if(photo.current&&!keepBasicSelections){const previous=photo.current.beds[0];rememberPhoto({...photo.current,beds:[{...previous,choice:model.choice,layout:model.layout,ladderFlipped:model.ladderFlipped??false,upgrades:{}}]});}
    update({direction,inspirationId:model.layout==='ernst'?'82662':model.layout==='castle'?'storybook':undefined,intent:undefined});
    setLandingStarted(true);setShowGallery(false);setExploring(true);
  }}/>;
  return (
    <main className={`bcj bcj-step-${step}${fastTrack && step === 5 ? " bcj-fast-track" : ""}${step === 7 && answers.intent === "deposit" ? " bcj-deposit-page" : ""}${savedDesign ? " bcj-saved-page" : ""}${compactCatalog ? " bcj-compact-catalog" : ""}${transformationFirst && step === 5 ? " bcj-transform-entry" : ""}`}>
      <header className="bcj-header">
        <a href="https://blissbunkbeds.com" aria-label="Bliss Bedrooms home">
          <img
            className="bcj-brand-logo"
            src="/bliss-bedrooms-logo-transparent.png"
            alt="Bliss Bedrooms"
            width="496"
            height="344"
          />
        </a>
        <span className="bcj-header-note">A room for your people.</span>
      </header>
      <div className="bcj-shell">
        <nav className="bcj-progress bcj-progress-labeled" aria-label="Your project progress">
          <ol>
            {(transformationFirst ? ["Your room", "Your details", requireVerificationCall ? "Your request" : "Your price", requireVerificationCall ? "Next steps" : hasPricedClassic ? "Review & pay" : "Your quote"] : JOURNEY_STEPS).map((label,i)=>{
              const current=progressIndex;
              const available=transformationFirst ? i===0 || (i===1&&(step>=6||contactCaptured)) || (i===2&&contactCaptured&&priceRevealed) || (i===3&&!!answers.intent) : i<=current;
              return <li key={label} className={i<current?"is-complete":i===current?"is-current":""}>
                <button type="button" aria-current={i===current?"step":undefined} disabled={busy||(!available&&i!==current)} onClick={()=>{
                  if(i===current)return;
                  if(!transformationFirst){go(i+1);return;}
                  if(i===0){go(5);return;}
                  if(i===1){go(6);return;}
                  if(i===2)update({intent:undefined});
                  go(7);
                }}><span className="bcj-step-number" aria-hidden="true">{i<current?"✓":i+1}</span><span>{label}</span></button>
              </li>;
            })}
          </ol>
          <p className="bcj-progress-note">Step {progressIndex + 1} of {transformationFirst ? 4 : JOURNEY_STEPS.length}</p>
        </nav>
        <section className="bcj-heading">
          <span className="bcj-eyebrow">
            {
              [
                "ROOM FOR YOUR PEOPLE",
                "EVERYDAY COMFORT",
                "YOUR ROOM, IN YOUR WORDS",
                "YOUR PERSONAL STARTING POINT",
                "MAKE IT YOURS",
                "YOUR PROJECT DETAILS",
                "A COMFORTABLE FIT",
              ][step - 1]
            }
          </span>
          <h1 ref={heading} tabIndex={-1}>
            {requireVerificationCall && step === 7 ? "Call for Price" : fastTrack && step===5 ? "Make it Yours" : showSalesLetter ? "From your idea to your finished room." : title}
          </h1>
          <p>
            {requireVerificationCall && step === 7 ? "A brief call to verify your project details and contact information comes before your quote." : fastTrack && step===5 ? "Choose your finish and extras. No room photo needed." : showSalesLetter
              ? "Your project is saved. Here’s what comes next."
              : savedDesign && !submitted ? "See the starting price. Then make it yours." : intro}
          </p>
        </section>
        {step === 7 && !savedDesign && answers.direction !== "classic" && answers.intent !== "deposit" && selectedProducts}
        {[2].includes(step) &&
          !(step === 7 && answers.intent === "room_review") && (
            <figure className={`bcj-mood bcj-mood-${step}`}>
              <img
                src={
                  step === 2
                    ? classicImage
                    : answers.direction === "one_of_a_kind"
                      ? CUSTOM_WORLDS[0].image
                      : answers.direction === "custom"
                        ? featured.image
                        : classicImage
                }
                alt={
                  answers.direction === "one_of_a_kind" && step !== 2
                    ? "Castle concept inspiration"
                    : "Bliss bedroom design inspiration"
                }
              />
              <figcaption>
                {answers.direction === "one_of_a_kind" && step !== 2
                  ? "Concept inspiration. Final design is tailored to your room."
                  : "Styled inspiration. Your room and scope are confirmed individually."}
              </figcaption>
            </figure>
          )}
        {step === 1 && (
          <div className="bcj-editorial">
            <div className="bcj-editorial-visuals">
            <figure className="bcj-portrait">
              <img
                src="/media/portfolio/curated-82662.jpg"
                alt="Bliss Ernst room with four sleeping places, warm timber rails and a central staircase"
              />
              <figcaption>
                Considered details. A welcoming place to stay.
              </figcaption>
            </figure>
            <RoomInspirationCollage />
            </div>
            <div className="bcj-question-panel">
              <fieldset>
                <legend>
                  Who will stay?{" "}
                  <small>
                    Choose everyone sharing the room — parents and children can
                    stay together.
                  </small>
                </legend>
                <div className="bcj-choices">
                  {AGE_GROUPS.map((group) => (
                    <Choice
                      key={group}
                      selected={answers.groups.includes(group)}
                      onClick={() =>
                        update({
                          groups: answers.groups.includes(group)
                            ? answers.groups.filter((g) => g !== group)
                            : [...answers.groups, group],
                        })
                      }
                    >
                      {group}
                    </Choice>
                  ))}
                </div>
              </fieldset>
              <div className="bcj-inline-fields">
                <label>
                  How many guests?
                  <select
                    value={largeGroup ? "more" : (answers.sleepers ?? "")}
                    onChange={(e) => {
                      setLargeGroup(e.target.value === "more");
                      if (e.target.value === "more") setLargeGuestInput("21");
                      update({
                        sleepers:
                          e.target.value === "more"
                            ? 21
                            : e.target.value
                              ? Number(e.target.value)
                              : null,
                      });
                    }}
                  >
                    <option value="">Help me decide</option>
                    {Array.from({ length: 20 }, (_, i) => (
                      <option value={i + 1} key={i + 1}>
                        {i + 1} {i === 0 ? "guest" : "guests"}
                      </option>
                    ))}
                    <option value="more">More than 20 guests</option>
                  </select>
                </label>
                {largeGroup && (
                  <label>
                    Exact guest count
                    <input
                      type="number"
                      inputMode="numeric"
                      min={21}
                      max={MAX_CLASSIC_GUESTS}
                      step={1}
                      value={largeGuestInput}
                      aria-label="Exact guest count"
                      aria-describedby="bcj-large-guest-help"
                      onChange={(e) => {
                        setLargeGuestInput(e.target.value);
                        const n = Number(e.target.value);
                        update({
                          sleepers:
                            Number.isInteger(n) &&
                            n >= 21 &&
                            n <= MAX_CLASSIC_GUESTS
                              ? n
                              : null,
                        });
                      }}
                    />
                    <small id="bcj-large-guest-help">
                      Enter the total: 21–{MAX_CLASSIC_GUESTS} guests.
                    </small>
                  </label>
                )}
                <label>
                  This room is for
                  <select
                    value={answers.purpose}
                    onChange={(e) =>
                      update({
                        purpose: e.target
                          .value as ConsultativeAnswers["purpose"],
                      })
                    }
                  >
                    <option value="family">Family &amp; friends</option>
                    <option value="rental">Vacation rental guests</option>
                    <option value="both">A little of both</option>
                  </select>
                </label>
              </div>
              {answers.sleepers !== 1 && !infantsOnly(answers) && (
                <fieldset className="bcj-sleeping-preferences">
                  <legend>
                    Would some guests comfortably share a queen bed?
                  </legend>
                  <div className="bcj-choice-grid">
                    {(
                      [
                        ["shared", "Yes, queen beds can be shared"],
                        ["separate", "Everyone needs their own mattress"],
                        ["mixed", "A mix of shared and individual beds"],
                        ["undecided", "Show me the possibilities"],
                      ] as const
                    ).map(([value, label]) => (
                      <Choice
                        key={value}
                        selected={answers.sharing === value}
                        onClick={() => update({ sharing: value })}
                      >
                        {label}
                      </Choice>
                    ))}
                  </div>
                  {answers.sharing === "mixed" && (
                    <label className="bcj-solo-field">
                      Guests who need their own mattress
                      <select
                        aria-label="Guests who need their own mattress"
                        value={Math.min(
                          answers.soloGuests,
                          answers.sleepers ?? 100,
                        )}
                        onChange={(e) =>
                          update({ soloGuests: Number(e.target.value) })
                        }
                      >
                        {Array.from(
                          { length: (answers.sleepers ?? 100) + 1 },
                          (_, n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                  )}
                  <p className="bcj-note">
                    We can often accommodate the same group with fewer bunk
                    units when suitable queen mattresses are shared.
                  </p>
                </fieldset>
              )}
              <p className="bcj-note">No contact details needed to explore.</p>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="bcj-comfort">
            <fieldset className="bcj-room-availability">
              <legend>How many rooms could make room for your guests?</legend>
              <div className="bcj-inline-fields">
                <label>
                  Dedicated bunk rooms
                  <select
                    aria-label="Dedicated bunk rooms"
                    value={answers.dedicatedRooms}
                    onChange={(e) =>
                      update({ dedicatedRooms: Number(e.target.value) })
                    }
                  >
                    {Array.from({ length: 101 }, (_, n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Mixed-use rooms
                  <select
                    aria-label="Mixed-use rooms"
                    value={answers.mixedRooms}
                    onChange={(e) =>
                      update({ mixedRooms: Number(e.target.value) })
                    }
                  >
                    {Array.from({ length: 101 }, (_, n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="bcj-note">
                A mixed-use room also serves as an office, playroom or living
                space. We’ll consider that purpose when planning the room.
              </p>
              {answers.dedicatedRooms + answers.mixedRooms === 0 && (
                <p role="status">Choose at least one available room.</p>
              )}
              {answers.dedicatedRooms + answers.mixedRooms > 100 && (
                <p role="status">Choose up to 100 rooms in total.</p>
              )}
            </fieldset>
            {!infantsOnly(answers) && (
              <fieldset className="bcj-room-availability">
                <legend>What would help your guests feel comfortable?</legend>
                <p className="bcj-note">
                  Guest build helps us consider mattress width alongside length.
                  Choose the closest description, or leave this for your
                  designer.
                </p>
                <div className="bcj-choice-grid">
                  {(
                    [
                      ["slender", "Mostly slender builds"],
                      ["average", "Mostly average builds"],
                      ["broader", "Broader frames / more sleeping width"],
                      ["mixed", "A mix of builds"],
                      ["undecided", "Let’s discuss sleeping space"],
                    ] as const
                  ).map(([value, label]) => (
                    <Choice
                      key={value}
                      selected={answers.guestBuild === value}
                      onClick={() => update({ guestBuild: value })}
                    >
                      {label}
                    </Choice>
                  ))}
                </div>
                <p className="bcj-note">
                  Sleeping capacity and personal comfort are different. Your
                  designer confirms the appropriate bed specifications and load
                  ratings; body build alone does not establish suitability.
                </p>
              </fieldset>
            )}
            {infantsOnly(answers) ? (
              <div className="bcj-letter">
                <span className="bcj-eyebrow">ROOM TO GROW</span>
                <h2>Let’s plan for their next chapter.</h2>
                <p>
                  Infants need their own appropriate infant sleep space. We can
                  explore Classic as a future direction, with a designer
                  reviewing ages and room needs before any bunk layout is
                  chosen.
                </p>
              </div>
            ) : (
              <>
                <fieldset>
                  <legend>How much legroom would feel right?</legend>
                  <div className="bcj-choice-grid">
                    {(
                      [
                        [
                          "standard",
                          "Standard length",
                          "For guests comfortable with standard mattress lengths.",
                        ],
                        [
                          "longer",
                          "A little extra length",
                          "Explore Twin XL or queen-length options.",
                        ],
                        [
                          "undecided",
                          "Help me decide",
                          "We’ll keep this open for your designer.",
                        ],
                      ] as const
                    ).map(([value, label, detail]) => (
                      <Choice
                        key={value}
                        selected={answers.legroom === value}
                        detail={detail}
                        onClick={() => update({ legroom: value })}
                      >
                        {label}
                      </Choice>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend>What would make getting into bed easier?</legend>
                  <div className="bcj-choice-grid">
                    {(
                      [
                        [
                          "stairs",
                          "Stairs",
                          "Worth exploring for older guests who will use an upper bunk.",
                        ],
                        [
                          "ladder",
                          "Ladder",
                          "A compact access option, subject to individual needs.",
                        ],
                        [
                          "lower_bed",
                          "Easy Access Lower Bunks",
                          "Perpendicular lower bunks, with room to access both sides of each bed.",
                        ],
                        [
                          "undecided",
                          "Let’s discuss it",
                          "Your designer can help assess the right approach.",
                        ],
                      ] as const
                    ).map(([value, label, detail]) => (
                      <Choice
                        key={value}
                        selected={answers.access === value}
                        detail={detail}
                        onClick={() => update({ access: value })}
                      >
                        {label}
                      </Choice>
                    ))}
                  </div>
                </fieldset>
                <p className="bcj-note">
                  Your designer confirms ages, access needs and clearances
                  before approving a layout.
                </p>
              </>
            )}
          </div>
        )}
        {step === 3 && (
          <div className="bcj-letter">
            <ul className="bcj-recap" aria-label="Your project summary">
              {repeatBackSummary(answers).map(({ label, detail }) => (
                <li key={label}>
                  <strong>{label}:</strong> {detail}
                </li>
              ))}
            </ul>
            <p className="bcj-recap-prompt">
              Have we understood you correctly?
            </p>
            <div className="bcj-confirm">
              <Choice
                selected={answers.confirmed}
                onClick={() => update({ confirmed: !answers.confirmed })}
              >
                Yes, that sounds like us.
              </Choice>
              <button
                type="button"
                className="bcj-text-button"
                onClick={() => go(1)}
              >
                I’d like to adjust something
              </button>
            </div>
          </div>
        )}
        {step === 3 && (
          <figure className="bcj-repeat-photo">
            <img
              src="/media/portfolio/curated-82316.jpg"
              alt="Bliss Drain room with white joinery, warm wood details and individual reading lights"
            />
          </figure>
        )}
        {step === 4 && (
          <>
            <article
              className="bcj-recommendation"
              aria-label="Our Classic recommendation"
            >
              <figure>
                <img
                  src={classicImage}
                  alt="Illustrative Classic reference with white joinery, stair access and layered neutral linens"
                />
                <figcaption>
                  Illustrative Classic reference. Your configuration and
                  included options are listed separately.
                </figcaption>
              </figure>
              <div>
                <span className="bcj-eyebrow">RECOMMENDED STARTING POINT</span>
                <h2>The Classic Collection</h2>
                <h3>{recommendation.title}</h3>
                <p>
                  {infantsOnly(answers)
                    ? "A starting point for the years ahead, with a separate infant sleep space for now."
                    : recommendation.explanation}
                </p>
                {!infantsOnly(answers) && answers.sleepers && (
                  <section
                    className="bcj-capacity-options"
                    aria-label="Ways to accommodate your guests"
                  >
                    <h3>Different ways to make room for {answers.sleepers}</h3>
                    <p className="bcj-note">
                      Choose the sleeping arrangement that suits your guests.
                      These options all stay within Classic.
                    </p>
                    <div className="bcj-choice-grid">
                      {(["shared", "separate"] as const).map((sharing) => {
                        const option = planGuestRooms(
                          answers.sleepers!,
                          sharing,
                          0,
                          answers.dedicatedRooms,
                          answers.mixedRooms,
                          answers.legroom === "longer",
                          answers.guestBuild === "broader",
                        );
                        return (
                          <Choice
                            key={sharing}
                            selected={answers.sharing === sharing}
                            onClick={() => update({ sharing })}
                            detail={`${describeBunkMix(option.choices)} · ${option.capacity} sleeping places. ${option.sharingGuests ? `${option.sharingGuests} guests share queens, two per mattress.` : "A separate mattress for each guest."}`}
                          >
                            {sharing === "shared"
                              ? "Fewer units, with sharing where needed"
                              : "Individual sleeping places"}
                          </Choice>
                        );
                      })}
                    </div>
                    <button
                      className="bcj-text-button"
                      type="button"
                      onClick={() => {
                        update({ sharing: "mixed" });
                        go(1);
                      }}
                    >
                      Plan a mix of shared and individual beds
                    </button>
                  </section>
                )}
                {answers.sleepers && !infantsOnly(answers) && (
                  <section
                    className="bcj-room-allocation"
                    aria-label="Proposed room distribution"
                  >
                    <h3>A possible room-by-room arrangement</h3>
                    {roomPlan.rooms.map((room, i) => (
                      <p key={i}>
                        <strong>{room.name}</strong> —{" "}
                        {room.choices.length
                          ? `${describeBunkMix(room.choices)} · ${room.guests} guests`
                          : "No bunk needed for this starting plan"}
                        {room.mixedUse && room.choices.length
                          ? ". Keep space for its daytime purpose."
                          : ""}
                      </p>
                    ))}
                    <p className="bcj-note">
                      A proposed distribution, not a room-fit assessment. Your
                      designer can move sleeping places between rooms after
                      reviewing dimensions and how each room is used.
                    </p>
                  </section>
                )}
                <p className="bcj-note">
                  {answers.access === "lower_bed"
                    ? "The reference photo shows the Classic aesthetic. Your requested perpendicular lower beds and access on both sides need a room-specific layout, confirmed by your designer."
                    : "Classic comes in several configurations. Two units can sit parallel, side by side, with stairs in the middle. Your designer confirms the room dimensions and each upper-bunk entry."}
                </p>
                <div className="bcj-recommendation-actions">
                  <button
                    type="button"
                    className="bcj-primary"
                    onClick={() => chooseDirection("classic")}
                  >
                    Explore my Classic direction <ArrowRight size={18} />
                  </button>
                  <button
                    type="button"
                    className="bcj-secondary"
                    onClick={() => {
                      update({ direction: "classic", intent: undefined });
                      go(6);
                    }}
                  >
                    I’m ready — show price &amp; payment{" "}
                    <ArrowRight size={18} />
                  </button>
                </div>
                <small className="bcj-note">
                  A recommendation to explore. Final layout approval rests with
                  your designer.
                </small>
              </div>
            </article>
            <section
              className="bcj-custom-alternatives"
              aria-label="Separate custom alternatives"
            >
              <span className="bcj-eyebrow">A DIFFERENT DIRECTION</span>
              <h2>These designs feel more suitable for my home:</h2>
              <p>
                Explore Custom as a separate direction from our Classic
                recommendation.
              </p>
              <div className="bcj-portfolio-cards">
                <button
                  type="button"
                  onClick={() => {
                    setPortfolio("custom");
                    setGalleryIndex(
                      adProject
                        ? CUSTOM_PROJECTS.findIndex(
                            (p) => p.id === adProject.id,
                          )
                        : 0,
                    );
                  }}
                >
                  <img
                    src={featured.image}
                    alt={`${featured.name}, a custom built-in Bliss room`}
                  />
                  <span>
                    <small>BLISS CUSTOM</small>
                    <strong>
                      {adProject
                        ? "The room from your ad"
                        : "Distinctive custom rooms"}
                    </strong>
                    <em>Explore Our Work →</em>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPortfolio("one_of_a_kind");
                    setGalleryIndex(0);
                  }}
                >
                  <img
                    src={CUSTOM_WORLDS[0].image}
                    alt={CUSTOM_WORLDS[0].alt}
                  />
                  <span>
                    <small>ONE OF A KIND · CONCEPT COLLECTION</small>
                    <strong>Castle &amp; imaginative worlds</strong>
                    <em>Explore Our Work →</em>
                  </span>
                </button>
              </div>
            </section>
          </>
        )}
        {portfolio && (
          <section
            ref={portfolioHost}
            className="bcj-portfolio-view"
            aria-label="Portfolio viewer"
          >
            <div className="bcj-portfolio-heading">
              <span className="bcj-eyebrow">
                {portfolio === "one_of_a_kind"
                  ? "CONCEPT INSPIRATION · NOT COMPLETED BUILDS"
                  : "FROM THE BLISS PORTFOLIO"}
              </span>
              <button
                type="button"
                className="bcj-text-button"
                onClick={() => setPortfolio(null)}
              >
                Close portfolio ×
              </button>
            </div>
            <img
              className="bcj-gallery-image"
              src={item.image}
              alt={item.name}
            />
            <div className="bcj-gallery-copy">
              <div>
                <h2>{item.name}</h2>
                <p>{item.detail}</p>
              </div>
              <div className="bcj-gallery-controls">
                <button
                  type="button"
                  aria-label="Previous project"
                  onClick={() =>
                    setGalleryIndex(
                      (galleryIndex + galleryItems.length - 1) %
                        galleryItems.length,
                    )
                  }
                >
                  ←
                </button>
                <span>
                  {galleryIndex + 1} / {galleryItems.length}
                </span>
                <button
                  type="button"
                  aria-label="Next project"
                  onClick={() =>
                    setGalleryIndex((galleryIndex + 1) % galleryItems.length)
                  }
                >
                  →
                </button>
              </div>
            </div>
            <button
              type="button"
              className="bcj-primary"
              onClick={() => chooseDirection(portfolio, item.id)}
            >
              This direction feels like me <ArrowRight size={18} />
            </button>
            <p className="bcj-note">
              Your answers stay with you. Your designer confirms what can work
              in your home.
            </p>
          </section>
        )}
        {step === 5 && (
          <>
            {!exploring && !transformationFirst && (
              <div className="bcj-transform-actions">
                <button
                  type="button"
                  className="bcj-text-button"
                  onClick={() => {
                    setExploring(true);
                    setShowGallery(false);
                  }}
                >
                  Transform My Room - Free
                </button>
              </div>
            )}
            {fastTrack && answers.direction==='classic' && <section className="basic-fast-track" aria-label="Customize your bunk without a room photo">
              <div className="fast-track-actions"><button type="button" className="bcj-text-button" onClick={()=>{setOpenPreviewEntry(false);setLandingStarted(false);}}>Back to collections</button><button type="button" className="bcj-text-button" onClick={()=>{setFastTrack(false);if(!photo.current?.photo){setOpenPreviewEntry(true);setLandingStarted(false);}window.scrollTo({top:0,behavior:"instant" as ScrollBehavior});}}>Preview in My Room →</button></div>
              <SelectedBunkHero model={LANDING_MODELS.Basic.find(m=>m.choice===bunks[0].choice)??landingModel}/>
              
              <BunkOptions showPrices={false} editableSizes showUpgradeIcons showPreview={false} bunks={bunks} onChange={next=>{
                setAdjustedBunks(next);setLandingModel(prior=>({...LANDING_MODELS.Basic.find(m=>m.choice===next[0].choice)!,ladderFlipped:prior.ladderFlipped}));setAccepted(false);setConsent(undefined);setSigner('');
                if(photo.current)rememberPhoto({...photo.current,beds:photo.current.beds.map((bed,i)=>({...bed,...next[i]}))});
              }}/>
              <button type="button" className="purchase-cta" disabled={bunks.some(b=>b.finish==='custom_color'&&!!b.customColorCode&&!/^SW\d{4}$/.test(b.customColorCode))} onClick={()=>{update({intent:undefined});go(contactCaptured && priceRevealed?7:6);}}>{requireVerificationCall ? "Request my quote →" : "See My Price →"}</button>
            </section>}
            {(!fastTrack || answers.direction!=='classic') && (exploring || transformationFirst) && (
              <section
                className="bcj-tool"
                aria-label="Your room transformation"
              >
                {transformationFirst && !photo.current?.photo && <button type="button" className="bcj-text-button" onClick={()=>setLandingStarted(false)}>Back to collections</button>}
                {transformationFirst&&answers.direction==='classic'&&<button type="button" className="bcj-text-button" onClick={()=>setFastTrack(true)}>Customize without a room photo →</button>}
                <div className="bcj-tool-heading">
                  <h2 ref={toolHeading} tabIndex={-1}>
                    Your room preview
                  </h2>
                  <button
                    type="button"
                    className="bcj-text-button"
                    onClick={() => {
                      setExploring(false);
                      setShowGallery(true);
                    }}
                  >
                    Browse rooms
                  </button>
                </div>
                <p className="bcj-note">
                  Your photo is processed to clear and align the room. Visual
                  proportions are approximate.
                </p>
                {!transformationFirst && bunks.length > 1 && (
                  <p className="bcj-note">
                    Previewing one Classic unit. Your multi-unit arrangement and
                    shared access remain for your designer to confirm.
                  </p>
                )}
                {answers.direction === "one_of_a_kind" && answers.inspirationId !== "storybook" ? <section className="bcj-theme-preview" aria-label="Selected theme">
                  <h2>{customName}</h2><img src={customImage} alt={customName + " concept"}/>
                  <p>{customDescription}</p><p className="bcj-note">Concept image · Your room layout will be designed with you.</p>
                  <div className="bcj-saved-links"><button className="bcj-text-button" onClick={() => update({direction:"classic",inspirationId:undefined})}>Change design</button>
                  <button className="bcj-primary" onClick={() => {update({intent:undefined});setPriceRevealed(true);setStep(7);}}>Plan My Theme Room →</button></div>
                </section> : <Suspense
                  fallback={<p role="status">Opening your room preview…</p>}
                >
                  <Photo
                    requireVerificationCall={requireVerificationCall}
                    onTheme={id=>{update({direction:"one_of_a_kind",inspirationId:id,intent:undefined});setPriceRevealed(true);setStep(7);}}
                    exploreOnly
                    embeddedEntry={transformationFirst}
                    initialFile={transformationFirst ? landingPhotoFile : undefined}
                    initialLayout={transformationFirst ? landingModel.layout : undefined}
                    initialLadderFlipped={transformationFirst ? landingModel.ladderFlipped : undefined}
                    initialDraft={photo.current}
                    initialChoice={bunks[0].choice}
                    initialBunks={bunks}
                    onPhoto={() => {}}
                    onDraftChange={rememberPhoto}
                    onBack={() => {
                      if (transformationFirst) {setLandingStarted(false);return;}
                      setExploring(false);
                      setShowGallery(true);
                    }}
                    onContinue={() => {
                      if (transformationFirst) {const selectedLayout=photo.current?.beds[0]?.layout;update({direction:selectedLayout==="castle"?"one_of_a_kind":selectedLayout==="ernst"?"custom":"classic",inspirationId:selectedLayout==="castle"?"storybook":selectedLayout==="ernst"?"82662":undefined,intent:undefined});}
                      else update({intent:undefined});
                      if(photo.current?.beds.some(b=>b.layout==="ernst") || answers.direction!=="classic") {setPriceRevealed(true);setStep(7);} else go(contactCaptured && priceRevealed ? 7 : 6);
                    }}
                  />
                </Suspense>}
              </section>
            )}
            {showGallery && !transformationFirst && (
              <section
                className="bcj-browse"
                aria-labelledby="bcj-browse-title"
              >
                <span className="bcj-eyebrow">ROOMS TO INSPIRE YOUR OWN</span>
                <h2 id="bcj-browse-title" ref={browseHeading} tabIndex={-1}>
                  Explore the rooms.
                </h2>
                <img src={galleryRoom.image} alt={galleryRoom.name} />
                <div aria-live="polite" className="bcj-gallery-caption">
                  <h3>{galleryRoom.name}</h3>
                  <p>{galleryRoom.description}</p>
                </div>
                <div className="bcj-gallery-controls">
                  <button
                    type="button"
                    className="bcj-secondary"
                    onClick={() =>
                      setGalleryIndex(
                        (galleryIndex + galleryRooms.length - 1) %
                          galleryRooms.length,
                      )
                    }
                  >
                    ← Previous room
                  </button>
                  <span>
                    {(galleryIndex % galleryRooms.length) + 1} /{" "}
                    {galleryRooms.length}
                  </span>
                  <button
                    type="button"
                    className="bcj-secondary"
                    onClick={() =>
                      setGalleryIndex((galleryIndex + 1) % galleryRooms.length)
                    }
                  >
                    Next room →
                  </button>
                </div>
              </section>
            )}
          </>
        )}
        {step === 6 && !showSalesLetter && (
          <section className="bcj-contact-stage" aria-label="Save your project">
            <aside className="bcj-contact-recap">
              <img
                src={
                  answers.direction === "classic"
                    ? classicImage
                    : answers.direction === "one_of_a_kind"
                      ? CUSTOM_WORLDS[0].image
                      : featured.image
                }
                alt="Your Bliss design direction"
              />
              <span className="bcj-eyebrow">YOUR PREFERENCES, TOGETHER</span>
              {summary}
              <p className="bcj-note">
                Your designer will confirm the room measurements and final
                layout.
              </p>
            </aside>
            <div className="bcj-final-action">
              <form onSubmit={(event) => submit(event, 6)}>
                {contactFields}
                {contactPermission}
                {error && (
                  <p className="bcj-error" role="alert">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  className="bcj-primary"
                  disabled={busy || !canContact}
                >
                  {busy ? "Saving your project…" : requireVerificationCall ? "Request my quote" : answers.direction === "classic" ? "See my price" : catalogPurchasable ? "See my packages" : "See my next steps"}
                  <ArrowRight size={18} />
                </button>
                <p className="bcj-note">
                  {requireVerificationCall ? "Next: a brief verification call, then your quote." : answers.direction === "classic" ? "Next: your price and payment options." : catalogPurchasable ? "Next: your packages and ways to pay." : "Next: your Room Review and tailored quote options."} Your room and selections stay saved.
                </p>
              </form>
            </div>
          </section>
        )}
        {requireVerificationCall && step === 7 && (
          <section className="bcj-saved-design" aria-label="Quote request">
            <div className="bcj-saved-description">
              {summary}
              <a className="bcj-primary" href="tel:+18508120704">Call for Price</a>
              {submitted && contactCaptured ? (
                <div className="bcj-saved-request" role="status">
                  <h2>Your request is with Bliss.</h2>
                  <p>We’ll call you at {details.phone} to verify your project details and contact information, then send your quote. A representative will be assigned to follow up.</p>
                  <button className="bcj-text-button" onClick={backToRoom}>Back to my room</button>
                </div>
              ) : <button className="bcj-text-button" onClick={() => go(6)}>Save my details and request a call</button>}
            </div>
          </section>
        )}
        {!requireVerificationCall && <>
        {step === 6 && showSalesLetter && contactCaptured && (
          <BlissPriceIntroduction
            onContinue={() => {
              setPriceRevealed(true);
              setShowSalesLetter(false);
              setStep(7);
            }}
          />
        )}
        {savedDesign && <section className="bcj-saved-design" aria-label="Your saved design">
          {compactCatalog ? <>
            <div className="bcj-catalog-recap">
              <img src={customImage} alt={customName + " example design"}/>
              <div><span className="bcj-eyebrow">{answers.direction === "one_of_a_kind" ? "BLISS THEMES" : "BLISS CUSTOM"}</span><h2>{customName}</h2><p>{answers.direction === "custom" ? "Central staircase." : "Your selected design."}</p></div>
            </div>
            <div className="bcj-saved-links bcj-catalog-links"><button className="bcj-text-button" onClick={backToRoom}>← Back to my room</button><button className="bcj-text-button" onClick={() => {setCustomCheckout(false);setExploreTier(answers.direction === 'one_of_a_kind' ? 'one_of_a_kind' : 'custom');}}>Change design</button></div>
          </> : <>
          <h2>{customName}</h2>
          <div className="bcj-saved-image">
            <img src={customImage} alt={customName + " example design"}/>
            <div className="bcj-saved-links">
              <button className="bcj-text-button" onClick={backToRoom}>← Back to my room</button>
              <button className="bcj-text-button" onClick={() => {setCustomCheckout(false);setExploreTier(answers.direction === 'one_of_a_kind' ? 'one_of_a_kind' : 'custom');}}>Change design</button>
            </div>
          </div>
          <div className="bcj-saved-description">
            <span className="bcj-eyebrow">YOUR SELECTION · {answers.direction === "one_of_a_kind" ? "BLISS THEMES" : "BLISS CUSTOM"}</span>
            <p>{customDescription}</p>
            {catalogPurchasable ? customCheckout && contactCaptured && savedPlanId ? null : <form className="bcj-saved-request" onSubmit={event=>{event.preventDefault();if(!contactCaptured)go(6);else void submit(event,6);}}>
              <h3>Make this {customName === 'Ernst' ? 'Ernst' : 'Castle'} yours.</h3><p>Choose your package, then choose how to pay.</p>
              {error && <p role="alert" className="bcj-error">{error}</p>}
              <button className="bcj-primary" disabled={busy}>{busy?'Saving your room…':'See packages & ways to pay'}<ArrowRight size={18}/></button>
              <p className="bcj-price-reassurance">Review your scope and agreement before paying. Room measurements and final fit are confirmed before fabrication.</p>
            </form> : submitted ? <div className="bcj-saved-request" role="status"><Check size={26}/><h3>Your request is with Bliss.</h3><p>We’ll review your room and follow up the same business day at <strong>{contactDestination}</strong>.</p><button className="bcj-text-button" onClick={backToRoom}>Keep exploring →</button></div> : <form className="bcj-saved-request" onSubmit={event => {if(!contactCaptured){event.preventDefault();go(6);}else void submit(event, 7, "designer");}}>
              <h3>{answers.direction === 'one_of_a_kind' ? 'Bliss Themes' : 'Bliss Custom'} · Starting at</h3>
              <div className="bcj-starting-price">{answers.direction === 'one_of_a_kind' ? selectedTheme.id === 'storybook' ? money(CASTLE_STARTING_PRICE) : '$29,995' : money(CUSTOM_STARTING_PRICE)}</div>
              <p>Final price depends on your room and extras.</p>
              {error && <p role="alert" className="bcj-error">{error}</p>}
              <button className="bcj-primary" disabled={busy || (contactCaptured && !canContact)}>{busy ? "Sending your request…" : "Request my custom quote"}<ArrowRight size={18}/></button>
              <p className="bcj-price-reassurance">We’ll review your room and send you a quote. No payment today.</p>
              <p className="bcj-price-reassurance">We’ll follow up the same business day.</p>
            </form>}
            <details className="bcj-planning-help"><summary>Want help planning?</summary>
              <p>Book a 30-minute Room Review for $149. Consultation only; credit follows the service terms.</p>
              <button className="bcj-text-button" onClick={() => chooseIntent("room_review")}>Book a Room Review — $149</button>
              <button className="bcj-text-button" onClick={() => chooseIntent("design_only")}>Explore Design Only</button>
            </details>
          </div>
          </>}
          {catalogPurchasable && customCheckout && contactCaptured && savedPlanId && <div className="bcj-catalog-checkout"><Suspense fallback={<p role="status">Opening your packages…</p>}><Proposal planId={savedPlanId} productLine="custom" initialContact={details} preferredPaymentMode={rushOrder ? "full" : undefined} embedded onEditOrder={()=>setCustomCheckout(false)} /></Suspense></div>}
          {compactCatalog && <details className="bcj-catalog-design-details"><summary>Design details &amp; planning help</summary><p>{customDescription}</p><p>Book a 30-minute Room Review for $149. Consultation only; credit follows the service terms.</p><button className="bcj-text-button" onClick={() => chooseIntent("room_review")}>Book a Room Review — $149</button><button className="bcj-text-button" onClick={() => chooseIntent("design_only")}>Explore Design Only</button></details>}
        </section>}
        {step === 7 && contactCaptured && priceRevealed && !answers.intent && !savedDesign && (
          <div className={`bcj-investment${!hasPricedClassic ? " bcj-quote-pending" : " bcj-has-payment-choices"}`}>
            <article className="bcj-price-card">


<h2>{hasPricedClassic ? "Your price" : "Get your price"}</h2>
              {pkg && <div className="bcj-full-price" aria-label="Your full price" aria-live="polite"><span>Your complete bunk order</span><strong>{money(pkg.total)}</strong><small>Includes your selected extras and delivery allowance. No tax added.</small></div>}
              {!transformationFirst &&
                answers.direction === "classic" &&
                answers.sharing === "undecided" &&
                answers.sleepers !== 1 &&
                !infantsOnly(answers) && (
                  <div className="bcj-letter">
                    <p>
                      Which sleeping arrangement should we price for your
                      project?
                    </p>
                    <button
                      className="bcj-secondary"
                      type="button"
                      onClick={() => update({ sharing: "shared" })}
                    >
                      Queen beds can be shared
                    </button>
                    <button
                      className="bcj-secondary"
                      type="button"
                      onClick={() => update({ sharing: "separate" })}
                    >
                      Everyone needs their own mattress
                    </button>
                    <button
                      className="bcj-text-button"
                      type="button"
                      onClick={() => {
                        update({ sharing: "mixed" });
                        go(1);
                      }}
                    >
                      Plan a mix
                    </button>
                  </div>
                )}
              {answers.direction === "classic" ? (
                <>
                  {pkg ? (
                    <>

                      {checkoutCode}

                    </>
                  ) : (
                    <p>
                      {(!transformationFirst && !answers.sleepers) || infantsOnly(answers)
                        ? "Let’s confirm the sleeping spaces together before we price your room. You can request a tailored quote without paying a deposit."
                        : "We couldn’t prepare a reliable estimate. You can request a completed quote without paying a deposit."}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>
                    {answers.direction === "one_of_a_kind"
                      ? CUSTOM_INVESTMENT_NOTICE
                      : "Custom room pricing depends on the architecture, finishes, installation and your room. We’ll prepare a tailored scope and price before asking for a build deposit."}
                  </p>

                </>
              )}
            </article>
            {hasPricedClassic && pkg && selectedProducts}
            {hasPricedClassic && pkg && (rushOrder && !changingPaymentMethod ? <div><button type="button" className="bcj-primary" onClick={()=>selectPayment("full")}>Review & pay in full · {money(pkg.total)} <ArrowRight size={18}/></button><button type="button" className="bcj-text-button" onClick={changePaymentMethod}>Change payment method</button></div> : <PaymentChoices originalTotal={promotion ? pkg.total + promotion.savings : undefined} total={pkg.total} months={answers.planMonths} onMonths={selectPlanMonths} onSelect={selectPayment}/>)}
            {hasPricedClassic && pkg && (
                      <details className="bcj-price-details">
                        <summary>Price &amp; delivery details</summary>
                      <label className="bcj-delivery-zip">
                        Project ZIP for delivery estimate
                        <input
                          inputMode="numeric"
                          autoComplete="postal-code"
                          maxLength={5}
                          value={details.zip ?? ""}
                          onChange={(e) =>
                            setDetails({
                              ...details,
                              zip: e.target.value.replace(/[^0-9]/g, ""),
                            })
                          }
                        />
                      </label>
                        <PackageScopeReview
                          compact
                          proposal={pricing!.proposal}
                          selected={packageId}
                          total={pkg.total}
                          deposit={paymentDue}
                        />
                      <p className="bcj-note">
                        Mattresses and linens are not included.
                      </p>
                      {details.zip &&
                        details.zip.length === 5 &&
                        blissZipServiceStatus(details.zip) !==
                          "full_service" && (
                          <p className="bcj-review-note">
                            Delivery and installation need a service-area review
                            for this ZIP. You can discuss Design Only or request
                            guidance.
                          </p>
                        )}
                      {requiresReview ? (
                        <p className="bcj-review-note">
                          Your designer will confirm the final layout, ages,
                          access and clearances with you. You can begin with the
                          deposit for the priced scope below; final drawings are
                          approved before final sizing and assembly.
                        </p>
                      ) : null}
                      <p className="bcj-note">
                        {rushOrder ? "Full payment" : "50% deposit"}: {money(paymentDue)}. {rushOrder ? "No remaining balance on the approved order. Rush availability requires confirmation." : "The remaining balance is due after fabrication, before delivery and installation."} Review the agreement before paying.
                      </p>
                      </details>
            )}
            <aside className="bcj-next-options">


              {!hasPricedClassic && answers.direction === "classic" && <form onSubmit={event => {if(!contactCaptured){event.preventDefault();go(6);}else void submit(event, 7, "designer");}}>
                <p>No payment today.</p>
                {error && <p role="alert" className="bcj-error">{error}</p>}
                <button type="submit" className="bcj-primary" disabled={busy || (contactCaptured && !canContact)}>{busy ? "Sending your request…" : "Request my price"}<ArrowRight size={18}/></button>
              </form>}
              {!hasPricedClassic && answers.direction !== "classic" && <>
                <p>Start with a 30-minute Room Review. We’ll discuss your {answers.direction==='one_of_a_kind'?'theme':'custom layout'} and prepare the next steps toward a tailored build quote.</p>
                <button type="button" className="bcj-primary" onClick={()=>chooseIntent('room_review')}>Book a Room Review — $149 <ArrowRight size={18}/></button>
                <p className="bcj-note">Consultation only, credited under the service terms. Your build scope and price are confirmed before a deposit.</p>
                <button type="button" className="bcj-text-button" onClick={()=>chooseIntent('designer')}>Ask for my room price</button>
              </>}
              <details className="bcj-purchase-alternative"><summary>Talk to a designer or explore Design Only</summary>
              <h3 className="bcj-conversation-heading">
                Prefer to talk first?
              </h3>
              <p>
                If a designer is available, would you like to speak about your
                project?
              </p>
              <button
                type="button"
                className="bcj-secondary"
                onClick={() => chooseIntent("designer")}
              >
                Yes, request a conversation <ArrowRight size={18} />
              </button>
              <p className="bcj-note">
                Clients who submit their project deposit receive priority when
                designer appointments are arranged. A specific time is confirmed
                separately.
              </p>
              <div className="bcj-soft-options">
                <button
                  type="button"
                  onClick={() => chooseIntent("room_review")}
                >
                  <strong>Book a Room Review — $149</strong>
                  <span>
                    A paid 30-minute consultation, credited toward your Bliss
                    project under the service terms.
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => chooseIntent("design_only")}
                >
                  <strong>
                    {designServicePrice
                      ? `Purchase Design Only — ${money(designServicePrice)}`
                      : "Request a Design Only quote"}
                  </strong>
                  <span>
                    Custom room design, complete parts list and cut list. Build
                    with your own contractor.
                  </span>
                </button>
                <button type="button" onClick={() => chooseIntent("save")}>
                  <strong>I’d like some time</strong>
                  <span>Keep my direction for another visit</span>
                </button>
              </div></details>
            </aside>
          </div>
        )}
        {step === 7 && contactCaptured && priceRevealed && answers.intent && !savedDesign && (
          <div className={`bcj-final${answers.intent === "deposit" || answers.intent === "room_review" || answers.intent === "designer" ? " bcj-checkout-refined" : ""}`}>
            <aside className="bcj-final-recap" hidden={submitted && !!savedPlanId && answers.intent === "deposit" && !!consent}>
              <button
                type="button"
                className="bcj-text-button"
                disabled={busy}
                onClick={changePaymentMethod}
              >
                {answers.intent === "deposit" || answers.intent === "monthly_plan" ? "← Change payment method" : "Back to my choices"}
              </button>
            </aside>
            <section className="bcj-final-action" aria-live="polite">
              {answers.intent === "room_review" ? (
                <Suspense
                  fallback={<p role="status">Opening your Room Review…</p>}
                >
                  <RoomReview
                    editorial={false}
                    contact={details}
                    beforeCheckout={async (contact) =>
                      onSubmit(
                        contact,
                        consultationQuickAnswers(answers, bunks),
                        {
                          ...snapshot,
                          quoteSettings: {
                            ...snapshot.quoteSettings,
                            zip: contact.zip ?? "",
                          },
                        },
                        photo.current,
                      )
                    }
                  />
                </Suspense>
              ) : answers.intent === "save" ? (
                <>
                  <h2>A little time to dream.</h2>
                  <p>Save your current selections with Bliss, then keep the project link to return to this room.</p>
                  {submitted && savedPlanId ? <a className="bcj-primary" href={`${window.location.pathname}?plan_id=${encodeURIComponent(savedPlanId)}`}>Open my saved project <Check size={18} /></a> : <form onSubmit={event => void submit(event, 7, "save")}>
                    {error && <p className="bcj-error" role="alert">{error}</p>}
                    <button className="bcj-primary" disabled={busy}>{busy ? "Saving your project…" : "Save my direction"}<Check size={18} /></button>
                  </form>}
                </>
              ) : submitted && savedPlanId ? (
                answers.intent === "monthly_plan" && hasPricedClassic ? (
                  <Suspense
                    fallback={
                      <p role="status">
                        Preparing your interest-free payment plan…
                      </p>
                    }
                  >
                    <MonthlyPayment
                      promoCode={promotion?.code}
                      planId={savedPlanId}
                      contact={details}
                      selectedPackage={packageId}
                      installationMonth={answers.installationMonth}
                      frequency={answers.paymentFrequency}
                      months={answers.planMonths}
                      upfrontCents={Math.round(answers.upfrontAmount * 100)}
                      onBack={() => {
                        setSubmitted(false);
                        update({ intent: undefined });
                      }}
                    />
                  </Suspense>
                ) : answers.intent === "design_only" && hasPricedClassic ? (
                  <Suspense
                    fallback={
                      <p role="status">Preparing Design Only payment…</p>
                    }
                  >
                    <DesignOnlyPayment
                      key={savedPlanId}
                      planId={savedPlanId}
                      contact={details}
                    />
                  </Suspense>
                ) : answers.intent === "deposit" && consent ? (
                  <Suspense
                    fallback={<p role="status">Opening secure payment…</p>}
                  >
                    <Proposal
                      embedded
                      orderReviewed
                      planId={savedPlanId}
                      productLine="basic"
                      selectedPackage={packageId}
                      initialContact={details}
                      checkoutSnapshots={roomSnapshots.map(
                        (r) => r.configuratorSnapshot,
                      )}
                      checkoutRoomNames={roomSnapshots.map((r) => r.name)}
                      promoCode={promotion?.code}
                      orderConsent={consent}
                      onEditOrder={changePaymentMethod}
                    />
                  </Suspense>
                ) : (
                  <div className="bcj-confirmed">
                    <span className="bcj-success-mark">
                      <Check size={26} />
                    </span>
                    <h2>
                      {answers.intent === "monthly_plan"
                        ? "Your payment-plan request is with Bliss."
                        : "Your request is with Bliss."}
                    </h2>
                    <p>
                      We’ve saved your answers, design direction and
                      preferences. The team will review your project and contact
                      you at <strong>{contactDestination}</strong>.
                    </p>
                    {answers.intent === "designer" && <p className="bcj-note">We’ll follow up the same business day.</p>}
                    <p className="bcj-note">
                      {answers.intent === "monthly_plan"
                        ? `We’ll review your ${answers.planMonths}-month plan, ${answers.paymentFrequency === "biweekly" ? "payments every two weeks" : "monthly payments"}, optional upfront payment and requested installation month. The balance must be paid in full before installation. No payment has been taken and no installation date is reserved by this request.`
                        : answers.intent === "design_only"
                          ? "Your Design Only scope and price will be confirmed before any payment."
                          : "A designer appointment has not yet been booked. We’ll confirm availability with you."}
                    </p>
                    <div className="bcj-after-request-actions">
                      {hasPricedClassic && (
                        <button
                          type="button"
                          className="bcj-primary"
                          onClick={() => chooseIntent("deposit")}
                        >
                          {rushOrder ? "Review & pay in full" : "Review & pay my deposit"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="bcj-secondary"
                        onClick={() => chooseIntent("room_review")}
                      >
                        Book a Room Review — $149
                      </button>
                      {designServicePrice > 0 && (
                        <button
                          type="button"
                          className="bcj-secondary"
                          onClick={() => chooseIntent("design_only")}
                        >
                          Purchase Design Only — {money(designServicePrice)}
                        </button>
                      )}
                    </div>
                    <button
                      className="bcj-secondary"
                      type="button"
                      onClick={() => {
                        setSubmitted(false);
                        go(5);
                      }}
                    >
                      Keep exploring
                    </button>
                  </div>
                )
              ) : (
                <form onSubmit={(event) => submit(event)}>
                  <h2>
                    {answers.intent === "deposit"
                      ? "Review & pay."
                      : answers.intent === "monthly_plan"
                        ? "Plan now. Make room later."
                        : answers.intent === "design_only"
                          ? "Your builder. A Bliss design."
                          : "Ready to ask for your price?"}
                  </h2>
                  <p>
                    {answers.intent === "deposit"
                      ? "Your room is one step closer."
                      : answers.intent === "monthly_plan"
                        ? "Tell us when you’d like your room ready. Choose monthly or biweekly installments, with the full balance paid before installation. Review your schedule and authorize a card securely with Stripe."
                        : answers.intent === "design_only"
                          ? `${DESIGN_ONLY_DESCRIPTION} ${designServicePrice ? `Your service estimate is ${money(designServicePrice)}. Review the server-confirmed price and pay securely next.` : "We’ll confirm your Custom scope and service price with you."}`
                          : "Click below to ask Bliss to price your room. We’ll use your saved room and choices, then follow up the same business day. You won’t pay anything now."}
                  </p>
                  {(answers.intent !== "deposit" || !depositAvailable) && <>
                  <details className="bcj-saved-contact">
                    <summary>
                      Your contact details
                    </summary>
                    <p>{details.name} · {details.email}</p>
                    {contactFields}
                  </details>
                  </>}
                  {answers.intent === "monthly_plan" && (
                    <div className="bcj-monthly-request">
                      <label>
                        Payment timeline
                        <select
                          value={answers.planMonths}
                          onChange={(e) =>
                            update({
                              planMonths: Number(e.target.value) as
                                | 3
                                | 6
                                | 9
                                | 12,
                              installationMonth: "",
                            })
                          }
                        >
                          <option value={3}>3 months</option>
                          <option value={6}>6 months</option>
                          <option value={9}>9 months</option>
                          <option value={12}>12 months</option>
                        </select>
                      </label>
                      <label>
                        Optional upfront payment
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          max={
                            pkg
                              ? pkg.total -
                                installmentCount(
                                  answers.paymentFrequency,
                                  answers.planMonths,
                                ) *
                                  0.5
                              : undefined
                          }
                          placeholder="0"
                          value={answers.upfrontAmount || ""}
                          onChange={(e) =>
                            update({
                              upfrontAmount: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </label>
                      <label>
                        Payment frequency
                        <select
                          value={answers.paymentFrequency}
                          onChange={(e) =>
                            update({
                              paymentFrequency: e.target.value as
                                | "monthly"
                                | "biweekly",
                            })
                          }
                        >
                          <option value="monthly">
                            Monthly · {answers.planMonths} payments
                          </option>
                          <option value="biweekly">
                            Every two weeks ·{" "}
                            {installmentCount("biweekly", answers.planMonths)}{" "}
                            payments
                          </option>
                        </select>
                      </label>
                      <label>
                        Preferred installation month
                        <select
                          required
                          value={answers.installationMonth}
                          onChange={(e) =>
                            update({ installationMonth: e.target.value })
                          }
                        >
                          <option value="">Choose a month</option>
                          {Array.from({ length: 36 }, (_, i) => {
                            const date = new Date(
                              `${earliestInstallMonth}-01T12:00:00Z`,
                            );
                            date.setUTCMonth(date.getUTCMonth() + i);
                            const value = date.toISOString().slice(0, 7);
                            return (
                              <option key={value} value={value}>
                                {new Intl.DateTimeFormat("en-US", {
                                  month: "long",
                                  year: "numeric",
                                  timeZone: "UTC",
                                }).format(date)}
                              </option>
                            );
                          })}
                        </select>
                      </label>
                      {monthlyEstimate && (
                        <p>
                          {installmentCount(
                            answers.paymentFrequency,
                            answers.planMonths,
                          )}{" "}
                          {answers.paymentFrequency === "biweekly"
                            ? "payments every two weeks"
                            : "monthly payments"}{" "}
                          of approximately <strong>{monthlyEstimate}</strong>{" "}
                          toward your current{" "}
                          <strong>{money(pkg!.total)}</strong> scope. Any unpriced work is confirmed in the written plan. No tax added.
                        </p>
                      )}
                      <p className="bcj-note">
                        Your requested month is a planning preference. Bliss
                        will confirm payment dates, terms and installation
                        availability before you commit.
                      </p>
                    </div>
                  )}
                  {answers.intent === "deposit" && depositAvailable && pkg && (
                    <div className="bcj-deposit-terms">
                      <section className="bcj-checkout-order" aria-label="Your order">
                        <h3>Your order</h3>
                        <div className="bcj-checkout-product"><div><h4>Bliss Basic</h4><span>{bunks.length} bunk {bunks.length === 1 ? "set" : "sets"}</span></div><button type="button" className="bcj-text-button" disabled={busy || !!consent} onClick={() => { update({intent: undefined}); go(7); }}>Edit</button></div>
                        <dl className="bcj-checkout-totals">
                          <div><dt>Order subtotal</dt><dd>{money(regularPkg!.total)}</dd></div>
                          {promotion && <div className="bcj-checkout-saving"><dt>{promotion.code} · 25% off</dt><dd>−{money(promotion.savings)}</dd></div>}
                          <div className="bcj-checkout-total"><dt>Order total</dt><dd>{money(pkg.total)}</dd></div>
                        </dl>
                      </section>
                      {selectedProducts}
                      {checkoutCode}
                      <section className="bcj-deposit-card" aria-label="Your payment">
                        <p>{rushOrder ? "Full payment due today" : "50% deposit due today"}</p>
                        <strong>{money(paymentDue)}</strong>
                        <dl><div><dt>Remaining balance</dt><dd>{money((Math.round(pkg.total * 100) - Math.round(paymentDue * 100)) / 100)}</dd></div></dl>
                        <p className="bcj-balance-note">{rushOrder ? "No remaining balance on this order. Rush timing requires confirmation." : "Due before delivery."}</p>
                      </section>
                      <p className="bcj-payment-note">No tax added. Includes the delivery allowance for ZIP {details.zip}.</p>
                  <details className="bcj-saved-contact">
                    <summary>
                      Your contact details
                    </summary>
                    <p>{details.name} · {details.email}</p>
                    {contactFields}
                  </details>
                      <details className="bcj-order-disclosure">
                        <summary>Order details</summary>
                        <p>Includes the delivery allowance for ZIP {details.zip}.</p>
                        <PackageScopeReview
                          compact
                          proposal={pricing!.proposal}
                          selected={packageId}
                          total={pkg.total}
                          deposit={paymentDue}
                        />
                        <ul>
                          {(rushOrder ? ["Pay in full for the approved scope. Rush availability is confirmed separately.", ...PURCHASE_HIGHLIGHTS.slice(1)] : PURCHASE_HIGHLIGHTS).map((text) => (
                            <li key={text}>{text}</li>
                          ))}
                        </ul>
                      </details>
                      <PurchaseAgreement
                        paymentMode={paymentMode}
                        compact
                        productLine="basic"
                        separatelyQuotedSelections={
                          pricing?.proposal.separatelyQuotedSelections
                        }
                      >
                      <label className="bcj-check">
                        <input
                          required
                          type="checkbox"
                          checked={accepted}
                          onChange={(e) => setAccepted(e.target.checked)}
                        />
                        I approve this order and agree to the Purchase
                        Agreement.
                      </label>
                      </PurchaseAgreement>
                      <label>
                        Type your full name to sign
                        <input
                          required
                          id="checkout-signer-name"
                          value={signer}
                          onChange={(e) => setSigner(e.target.value)}
                        />
                      </label>
                      <p className="bcj-note">
                        Your typed name is your electronic signature. Payment is
                        processed by Stripe. Your designer confirms the final
                        room fit.
                      </p>
                    </div>
                  )}
                  {contactPermission}
                  {error && (
                    <p className="bcj-error" role="alert">
                      {error}
                    </p>
                  )}
                  <button
                    className="bcj-primary"
                    disabled={
                      busy ||
                      !canContact ||
                      (answers.intent === "deposit" &&
                        (!depositAvailable || !accepted || !signer.trim()))
                    }
                  >
                    {busy
                      ? "Saving your project…"
                      : answers.intent === "deposit"
                        ? `${rushOrder ? "Pay in full" : "Pay deposit"} · ${money(paymentDue)}`
                        : answers.intent === "design_only" && hasPricedClassic
                          ? "Review Design Only & pay"
                          : answers.intent === "monthly_plan"
                            ? "Review my payment plan & save a card"
                            : "Ask Bliss for my price"}{" "}
                    <ArrowRight size={18} />
                  </button>
                  {answers.intent === "deposit" && !depositAvailable && (
                    <p role="alert">
                      {!/^\d{5}$/.test(details.zip ?? "")
                        ? "Enter your project ZIP above to confirm delivery and your deposit."
                        : pricing?.pending
                          ? "An unpriced selection needs a confirmed price. Your paid Room Review remains available below."
                          : "Delivery and installation for this ZIP need confirmation. You can purchase Design Only or a Room Review now."}
                    </p>
                  )}
                  {answers.intent === "deposit" && !depositAvailable && (
                    <div className="bcj-after-request-actions">
                      {designServicePrice > 0 && (
                        <button
                          type="button"
                          className="bcj-secondary"
                          onClick={() => chooseIntent("design_only")}
                        >
                          Purchase Design Only — {money(designServicePrice)}
                        </button>
                      )}
                      <button
                        type="button"
                        className="bcj-secondary"
                        onClick={() => chooseIntent("room_review")}
                      >
                        Book a Room Review — $149
                      </button>
                    </div>
                  )}
                  {answers.intent !== "deposit" && <p className="bcj-note">
                    Your answers travel with your project. No need to start
                    over.
                  </p>}
                </form>
              )}
            </section>
          </div>
        )}
        </>}
        {step === 7 && priceRevealed && (!answers.intent || savedDesign) && <section className="bcj-pricing-tiers" aria-label="Pricing tiers">
          <h2>Find your fit.</h2><p>Our pricing tiers, at a glance.</p>
          <div className="bcj-tier-grid">{([
            {id:'classic',name:'Bliss Basic',description:'Standard-size bunks, freestanding or built in. Installation options for wood or metal wall studs.',level:'$$',price:money(BASIC_BED_PRICE),image:'/mockups/full-over-full-floor-flush.png'},
            {id:'custom',name:'Bliss Custom',description:'Built-in bunks tailored to your room’s dimensions, finishes and architecture.',level:'$$$',price:money(CUSTOM_STARTING_PRICE),image:selectedCustom.image},
            {id:'one_of_a_kind',name:'Bliss Themes',description:'One-of-a-kind themed rooms with imaginative structures and custom details, from castles to ships.',level:'$$$$',price:money(CASTLE_STARTING_PRICE),image:CUSTOM_WORLDS[0].image},
          ] as const).map(tier => answers.direction === tier.id ? <div className="bcj-tier-selected" key={tier.id} aria-current="true"><img src={tier.image} alt={tier.name + " example design"}/><span>{tier.level}</span><h3>{tier.name}</h3><p className="bcj-tier-description">{tier.description}</p><p>{requireVerificationCall ? "Pricing" : "Starting at"}</p><strong>{requireVerificationCall ? "Call for Price" : tier.price}</strong><small>Your selection</small></div> : <button type="button" key={tier.id} aria-label={`Explore ${tier.name}`} onClick={()=>setExploreTier(tier.id)}><img src={tier.image} alt={tier.name + " example design"}/><span>{tier.level}</span><h3>{tier.name}</h3><p className="bcj-tier-description">{tier.description}</p><p>{requireVerificationCall ? "Pricing" : "Starting at"}</p><strong>{requireVerificationCall ? "Call for Price" : tier.price}</strong><small>Explore →</small></button>)}</div>
          <p className="bcj-tier-note">Note: Final pricing depends on changes to the scope of work, premium finishes, bed and staircase storage, trim and lighting packages, custom features, project complexity, and delivery and installation logistics.</p>
        </section>}
        {exploreTier && <div className="bcj-tier-overlay" onKeyDown={event=>{if(event.key==='Escape')setExploreTier(null);}}><section role="dialog" aria-modal="true" aria-label="Explore designs" className="bcj-tier-dialog">
          <div className="bcj-tier-dialog-heading"><h2>{exploreTier==='classic'?'Bliss Basic':exploreTier==='custom'?'Bliss Custom':'Bliss Themes'}</h2><button autoFocus type="button" className="bcj-text-button" onClick={()=>setExploreTier(null)}>Close</button></div>
          <p>Your room photo stays saved. Choose a design.</p>
          {exploreTier==='classic' ? BED_OPTIONS.map((bed,choice)=><button className="bcj-tier-choice" type="button" key={choice} onClick={()=>selectTierDesign('classic',choice)}><BunkReferenceImages choices={[choice]} photos={selectionPhotos}/><span>{bed.name}<small>Try in my room →</small></span></button>) : exploreTier==='custom' ? <button className="bcj-tier-choice" onClick={()=>selectTierDesign('custom',0,'82662')}><img src={selectedCustom.image} alt="Ernst"/><span>Ernst<small>View design →</small></span></button> : CUSTOM_WORLDS.filter(world=>world.id==='storybook'||world.id==='voyage').map(world=><button className="bcj-tier-choice" key={world.id} onClick={()=>selectTierDesign('one_of_a_kind',0,world.id)}><img src={world.image} alt={world.alt}/><span>{world.id==='storybook'?'Castle Bunks':'Ship Bunks'}<small>View design →</small></span></button>)}
        </section></div>}
        {!savedDesign && (step < 7 || (step === 7 && !answers.intent)) && !(step === 5 && exploring) && (
          <footer className="bcj-footer">
            <button
              type="button"
              className="bcj-back"
              disabled={
                step === 1 || (transformationFirst && step === 5) || busy
              }
              onClick={() => go(step === 6 ? 5 : step - 1)}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <div>
              {step === 5 && (
                <span>Free to explore. A brief verification call before your quote.</span>
              )}
              {step < 6 && step !== 4 && (
                <button
                  type="button"
                  className="bcj-primary"
                  disabled={
                    !stageReady(step, answers) ||
                    (step === 1 &&
                      largeGroup &&
                      (!answers.sleepers || answers.sleepers < 21))
                  }
                  onClick={() => go(step + 1)}
                >
                  {step === 3
                    ? "See our recommendation"
                    : step === 5
                      ? (requireVerificationCall ? "Request my quote" : "See My Price")
                      : "Continue"}{" "}
                  <ArrowRight size={18} />
                </button>
              )}
              {step === 4 && (
                <button
                  type="button"
                  className="bcj-text-button"
                  onClick={() => chooseDirection("classic")}
                >
                  Stay with our Classic recommendation →
                </button>
              )}
              {step === 7 && contactCaptured && priceRevealed && (
                <button
                  type="button"
                  className="bcj-text-button"
                  onClick={() => chooseIntent("save")}
                >
                  Save and consider it →
                </button>
              )}
            </div>
          </footer>
        )}
        <div className="bcj-signoff"><img className="bcj-signoff-daydreamer" src="/brand/daydreamer-logo.webp" alt="DayDreamer 1.0" width="2048" height="683"/><p>Spaces is our Specialty</p></div>
      </div>
    </main>
  );
}
